import { useState, useEffect, useCallback } from "react";
import { Toaster, toast } from "react-hot-toast";
import {
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../context/AuthContext";
import {
  ACCESS,
  MODULE_GROUPS,
  normalizeAccess,
} from "../constants/permissionCatalog";

// User Management APIs
import {
  getUsers,
  deleteUser,
  getUserPermissionByUser,
} from "../api/workOrder";

// Permissions APIs
import {
  getPermissions,
  createRolePermission,
  getRolePermissions,
  createUserPermission,
  getUserPermission,
} from "../api/workOrder";

// Modals
import UpdateProfileModal from "../modals/UpdateProfileModal";
import InviteUserModal from "../modals/InviteUserModal";
import DeleteConfirmModal from "../modals/DeleteConfirmModal";

// Default fallback roles
const defaultRoles = [
  {
    role: "viewer",
    canViewWorkOrders: false,
    canEditWorkOrders: false,
    canViewAnalytics: false,
    canManageOnHold: false,
    canManageTransfers: false,
    canManageUsers: false,
    canManageProducts: false,
    canManageRoles: false,
  },
  {
    role: "editor",
    canViewWorkOrders: true,
    canEditWorkOrders: true,
    canViewAnalytics: true,
    canManageOnHold: true,
    canManageTransfers: true,
    canManageUsers: false,
    canManageProducts: false,
    canManageRoles: false,
  },
];

const PermissionToggle = ({ checked, onChange, label }) => (
  <div className="flex justify-center">
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="h-5 w-5 text-black focus:ring-gray-500 border-gray-300 rounded cursor-pointer"
      aria-label={label}
    />
  </div>
);

const AccessSelector = ({ value, onChange, name }) => {
  const current = normalizeAccess(value);

  const options =
    name === "Export Excel"
      ? [
          { key: ACCESS.NO_ACCESS, label: "No Access" },
          { key: ACCESS.READ, label: "Download" },
        ]
      : [
          { key: ACCESS.NO_ACCESS, label: "No Access" },
          { key: ACCESS.READ, label: "Read" },
          { key: ACCESS.WRITE, label: "Write" },
        ];

  return (
    <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
      {options.map((opt) => (
        <button
          key={opt.key}
          type="button"
          onClick={() => onChange(opt.key)}
          className={`px-3 py-1 text-xs font-medium transition ${
            current === opt.key
              ? "bg-black text-white"
              : "bg-white text-gray-700 hover:bg-gray-100"
          }`}
          aria-label={`${name} ${opt.label}`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
};

const PermissionsTable = ({
  items,
  columns,
  onToggle,
  onToggleAll,
  itemKey,
  displayKey,
  transposed = false,
}) => {
  if (!items || items.length === 0)
    return <p className="text-gray-500">No data available</p>;

  if (!transposed) {
    // For user modal (single row)
    const currentItem = items[0] || {};
    const allChecked = columns.every((col) => !!currentItem[col.key]);
    const someChecked = columns.some((col) => !!currentItem[col.key]);

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Permission
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div className="flex items-center justify-center gap-2">
                  Allowed
                  <input
                    type="checkbox"
                    checked={allChecked}
                    indeterminate={someChecked && !allChecked}
                    onChange={(e) => onToggleAll?.(e.target.checked)}
                    className="h-5 w-5 text-black focus:ring-gray-500 border-gray-300 rounded cursor-pointer"
                    aria-label="Toggle all permissions"
                  />
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {columns.map((col) => {
              const item = items[0];
              return (
                <tr key={col.key}>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">
                      {col.label}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {col.description || "No description"}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <PermissionToggle
                      checked={!!item[col.key]}
                      onChange={() => onToggle(col.key)}
                      label={`${col.label} for ${item[displayKey]}`}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  // Horizontal matrix (roles) - unchanged
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-8 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-80">
              Permission
            </th>
            {items.map((item) => (
              <th
                key={item[itemKey]}
                className="px-8 py-4 text-center text-xs font-medium text-gray-700 uppercase tracking-wider"
              >
                {item[displayKey]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {columns.map((col) => (
            <tr key={col.key}>
              <td className="px-8 py-4 align-top w-80">
                <div className="text-sm font-medium text-gray-900">
                  {col.label}
                </div>
                <div className="text-xs text-gray-500 mt-1 max-w-md">
                  {col.description || "No description available"}
                </div>
              </td>
              {items.map((item) => (
                <td
                  key={`${item[itemKey]}-${col.key}`}
                  className="px-8 py-4 text-center"
                >
                  <PermissionToggle
                    checked={!!item[col.key]}
                    onChange={() => onToggle(item[itemKey], col.key)}
                    label={`${col.label} for ${item[displayKey]}`}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────────

const UserAndPermissionsManagement = () => {
  const { canRead, canWrite } = useAuth();
  const canManageUsers = canWrite("ManageUsers");
  const canManagePermissions = canWrite("ManagePermissions");
  const canViewPermissions = canRead("ManagePermissions");

  const [activeTab, setActiveTab] = useState("users");
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const triggerRefresh = useCallback(() => setRefreshTrigger((k) => k + 1), []);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <Toaster />

      <h1 className="text-3xl font-bold text-gray-900 mb-8">
        User & Permissions Management
      </h1>

      <div className="border-b border-gray-200 mb-8">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab("users")}
            className={`pb-4 px-1 text-lg font-medium border-b-2 ${
              activeTab === "users"
                ? "border-black text-black"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Users Info
          </button>
          {/* <button
            onClick={() => setActiveTab("permissions")}
            disabled={!canManagePermissions}
            className={`pb-4 px-1 text-lg font-medium border-b-2 ${
              activeTab === "permissions"
                ? "border-black text-black"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            } ${!canManagePermissions ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            Role Permissions
          </button> */}
        </nav>
      </div>

      {activeTab === "users" && (
        <UsersTab
          canManageUsers={canManageUsers}
          canManagePermissions={canManagePermissions}
          canViewPermissions={canViewPermissions}
          onRefresh={triggerRefresh}
        />
      )}

      {activeTab === "permissions" && canManagePermissions && (
        <RolePermissionsTab refreshTrigger={refreshTrigger} />
      )}

      {activeTab === "permissions" && !canManagePermissions && (
        <div className="bg-white p-10 rounded-xl shadow-lg border border-red-200 text-center max-w-md mx-auto mt-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Access Denied
          </h2>
          <p className="text-gray-600 mb-2">
            You do not have permission to manage roles and permissions.
          </p>
          <p className="text-sm text-gray-500">
            Required: <strong>ManagePermissions</strong>
          </p>
        </div>
      )}
    </div>
  );
};

// ── Tab 1: Users & Invites ──────────────
const UsersTab = ({
  canManageUsers,
  canManagePermissions,
  canViewPermissions,
  onRefresh,
}) => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterRole, setFilterRole] = useState("All");
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    userId: null,
    userName: null,
  });

  // Permission modal states
  const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState(false);
  const [selectedUserForPerms, setSelectedUserForPerms] = useState(null);
  const [userPermissionChanges, setUserPermissionChanges] = useState({});
  const [permissionColumns, setPermissionColumns] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);

  const usersPerPage = 25;

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await getUsers();
      if (!res.data || !Array.isArray(res.data))
        throw new Error("Invalid users data");
      const fetched = res.data.map((u) => ({
        id: u._id,
        firstName: u.firstName || "",
        lastName: u.lastName || "",
        email: u.email,
        phone: u.phone || "",
        role: u.role || "viewer",
        modules: u.modules || "None",
      }));
      setUsers(fetched);
      setFilteredUsers(fetched);
      setCurrentPage(1);
      onRefresh();
    } catch (err) {
      toast.error("Failed to load users");
      setUsers([]);
      setFilteredUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    let list = [...users];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (u) =>
          (u.firstName || "").toLowerCase().includes(q) ||
          (u.lastName || "").toLowerCase().includes(q) ||
          u.email.toLowerCase().includes(q),
      );
    }
    if (filterRole !== "All") {
      list = list.filter((u) => u.role === filterRole);
    }
    setFilteredUsers(list);
    setCurrentPage(1);
  }, [searchQuery, filterRole, users]);

  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  const currentUsers = filteredUsers.slice(
    (currentPage - 1) * usersPerPage,
    currentPage * usersPerPage,
  );

  const paginate = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const max = 5;
    let start = Math.max(1, currentPage - Math.floor(max / 2));
    let end = Math.min(totalPages, start + max - 1);
    if (end - start + 1 < max) start = Math.max(1, end - max + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const handleUpdateClick = (user) => {
    setSelectedUser(user);
    setIsUpdateModalOpen(true);
  };

  const openDeleteModal = (id, name) =>
    setDeleteModal({ isOpen: true, userId: id, userName: name });
  const closeDeleteModal = () =>
    setDeleteModal({ isOpen: false, userId: null, userName: null });

  const handleDeleteConfirm = async () => {
    if (!deleteModal.userId) return;
    setDeletingId(deleteModal.userId);
    try {
      await deleteUser(deleteModal.userId);
      toast.success("User deleted");
      fetchUsers();
      closeDeleteModal();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete user");
    } finally {
      setDeletingId(null);
    }
  };

  const getDisplayName = (u) =>
    `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email;
  const getDisplayEmail = (u) =>
    `${u.email || ""} `.trim() || u.firstName || u.lastName;

  const shouldShowUpdate = (u) => !!(u.firstName || u.lastName || u.phone);

  // ── Permission Modal ────────────────────────────────────────────────────────
  const openPermissionsModal = async (user) => {
    setSelectedUserForPerms(user);
    setUserPermissionChanges({});
    setModalLoading(true);
    setIsPermissionsModalOpen(true);

    try {
      const permRes = await getPermissions();
      if (!permRes.data || !Array.isArray(permRes.data)) {
        toast.error("No permissions received");
        return;
      }

      const cols = permRes.data.map((p) => ({
        resource: p.resource,
        id: p._id,
        moduleKey: p.moduleKey || null,
        isModule: !!p.isModule,
      }));
      setPermissionColumns(cols);

      const userPermRes = await getUserPermissionByUser(user.id);

      const userData = userPermRes.data;

      const permObj = {};
      cols.forEach((col) => {
        const found = userData.permissions.find((p) => p._id === col.id);
        permObj[col.resource] = normalizeAccess(
          found?.access ?? found?.allowed ?? ACCESS.NO_ACCESS,
        );
      });

      setUserPermissionChanges(permObj);
    } catch (err) {
      toast.error("Failed to load permissions");
      console.error(err);
    } finally {
      setModalLoading(false);
    }
  };

  const handleAccessChangeInModal = (resourceKey, access) => {
    setUserPermissionChanges((prev) => {
      const next = { ...prev, [resourceKey]: access };

      // ── Bottom-up cascade (mirror child's access to parent) ─────────────
      // When a sub-module is changed, reflect the exact same access level on
      // its parent module — mirroring the top-down cascade behavior.
      // Rules:
      //   • Child → Read  ⇒ Parent becomes Read
      //   • Child → Write ⇒ Parent becomes Write
      //   • Child → No Access ⇒ Parent is left untouched (siblings may still
      //     need access; use the parent-level selector to clear everything).
      const parentGroup = MODULE_GROUPS.find((group) =>
        group.items.some((item) => item.resource === resourceKey),
      );

      if (parentGroup) {
        const targetAccess = normalizeAccess(access);
        if (targetAccess !== ACCESS.NO_ACCESS) {
          next[parentGroup.moduleResource] = targetAccess;
        }
      }

      return next;
    });
  };

  const handleModuleCascade = (moduleResource, access) => {
    const moduleDef = MODULE_GROUPS.find(
      (m) => m.moduleResource === moduleResource,
    );
    if (!moduleDef) return;

    setUserPermissionChanges((prev) => {
      const next = { ...prev, [moduleResource]: access };
      moduleDef.items.forEach((item) => {
        next[item.resource] = access;
      });
      return next;
    });
  };

  const handleSaveUserPermissions = async () => {
    if (!selectedUserForPerms || !permissionColumns.length) return;

    try {
      await createUserPermission({
        userId: selectedUserForPerms.id,
        permissions: permissionColumns.map((col) => ({
          permissionId: col.id,
          access: normalizeAccess(userPermissionChanges[col.resource]),
        })),
      });
      toast.success("User permissions updated");
      setIsPermissionsModalOpen(false);
      onRefresh();
      fetchUsers();
    } catch (err) {
      toast.error("Failed to save permissions");
    }
  };

  return (
    <div>
      {/* Search & Filter Bar */}
      <div className="flex flex-col gap-4 p-4 sticky top-0 z-10 bg-gray-50 -mx-6 px-6 shadow-sm mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:gap-4">
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full sm:flex-1 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-sm"
          />
          <div className="flex flex-col sm:flex-row sm:gap-4 mt-4 sm:mt-0">
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="w-full sm:w-auto p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-sm"
            >
              <option value="All">All Roles</option>
              <option value="viewer">Viewer</option>
              <option value="editor">Editor</option>
              <option value="admin">Admin</option>
            </select>
            {canManageUsers && (
              <button
                onClick={() => setIsInviteModalOpen(true)}
                className="w-full sm:w-auto p-3 bg-black text-white rounded-lg hover:bg-gray-900 text-sm font-medium mt-4 sm:mt-0"
              >
                Invite User
              </button>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <ArrowPathIcon className="mx-auto h-8 w-8 text-black animate-spin" />
          <p className="mt-4 text-gray-600">Loading users...</p>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="text-center py-12 text-gray-600">No users found.</div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full border-collapse">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  <th className="py-3 px-4 text-left text-gray-700 font-semibold border-b">
                    First Name
                  </th>
                  <th className="py-3 px-4 text-left text-gray-700 font-semibold border-b">
                    Last Name
                  </th>
                  <th className="py-3 px-4 text-left text-gray-700 font-semibold border-b">
                    Email
                  </th>
                  <th className="py-3 px-4 text-left text-gray-700 font-semibold border-b">
                    Phone
                  </th>
                  <th className="py-3 px-4 text-left text-gray-700 font-semibold border-b">
                    Role
                  </th>
                  <th className="py-3 px-4 text-left text-gray-700 font-semibold border-b">
                    User Access
                  </th>
                  <th className="py-3 px-4 text-left text-gray-700 font-semibold border-b">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentUsers.map((user) => (
                  <tr key={user.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4">{user.firstName}</td>
                    <td className="py-3 px-4">{user.lastName}</td>
                    <td className="py-3 px-4">{user.email}</td>
                    <td className="py-3 px-4">{user.phone}</td>
                    <td className="py-3 px-4">
                      <span
                        className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
                          user.role === "admin"
                            ? "bg-green-100 text-green-800"
                            : user.role === "editor"
                              ? "bg-blue-100 text-blue-800"
                              : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {user.role}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      {(() => {
                        const mods = user.modules || "None";
                        if (mods === "None") {
                          return (
                            <span className="inline-flex px-2.5 py-1 mr-1 mb-1 rounded-full text-xs font-medium text-red-600">
                              No Access
                            </span>
                          );
                        }

                        return mods.split(", ").map((mod, idx) => {
                          const trimmed = mod.trim();

                          let className = "bg-gray-100 text-gray-800"; 

                          if (trimmed === "ERPag") {
                            className = "bg-blue-100 text-blue-800";
                          } else if (trimmed === "AceLynk") {
                            className = "bg-purple-100 text-purple-800";
                          } else if (trimmed === "Costing Tool") {
                            className = "bg-green-100 text-green-800";
                          } else if (trimmed === "User Permission Management") {
                            className = "bg-amber-100 text-amber-800";
                          }

                          return (
                            <span
                              key={idx}
                              className={`inline-flex px-2.5 py-1 mr-1 mb-1 rounded-full text-xs font-medium ${className}`}
                            >
                              {trimmed}
                            </span>
                          );
                        });
                      })()}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-2 flex-wrap">
                        {shouldShowUpdate(user) && canManageUsers && (
                          <button
                            onClick={() => handleUpdateClick(user)}
                            className="px-3 py-1 bg-[#365daf] text-white rounded text-xs hover:bg-blue-800"
                          >
                            Update Profile
                          </button>
                        )}
                        {canViewPermissions && (
                          <button
                            onClick={() => openPermissionsModal(user)}
                            className="px-3 py-1 bg-black text-white rounded text-xs hover:bg-gray-800"
                          >
                            Permissions
                          </button>
                        )}
                        {canManageUsers && user.role !== "admin" && (
                          <button
                            onClick={() =>
                              openDeleteModal(user.id, getDisplayName(user))
                            }
                            disabled={deletingId === user.id}
                            className="px-3 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
                          >
                            <TrashIcon className="h-4 w-4" />
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="sm:hidden space-y-4">
            {currentUsers.map((user) => (
              <div
                key={user.id}
                className="bg-white p-4 rounded-lg shadow border"
              >
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h4 className="font-semibold">{getDisplayName(user)}</h4>
                    <p className="text-sm text-gray-600">{user.email}</p>
                  </div>
                  <span
                    className={`text-xs px-2 py-1 rounded-full font-medium ${
                      user.role === "admin"
                        ? "bg-green-100 text-green-800"
                        : user.role === "editor"
                          ? "bg-blue-100 text-blue-800"
                          : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {user.role}
                  </span>
                </div>
                {user.phone && (
                  <p className="text-sm text-gray-700 mb-3">
                    Phone: {user.phone}
                  </p>
                )}
                <p className="text-sm text-gray-700 mb-3">
                  User Access:{" "}
                  {(() => {
                    const mods = user.modules || "None";
                    if (mods === "None") {
                      return (
                        <span className="text-red-600 font-medium">
                          No Access
                        </span>
                      );
                    }
                    return <span className="font-medium">{mods}</span>;
                  })()}
                </p>
                <div className="flex flex-wrap gap-2">
                  {shouldShowUpdate(user) && canManageUsers && (
                    <button
                      onClick={() => handleUpdateClick(user)}
                      className="px-3 py-1 bg-black text-white rounded text-sm"
                    >
                      Update Profile
                    </button>
                  )}
                  {canViewPermissions && (
                    <button
                      onClick={() => openPermissionsModal(user)}
                      className="px-3 py-1 bg-indigo-600 text-white rounded text-sm"
                    >
                      Permissions
                    </button>
                  )}
                  {canManageUsers && user.role !== "admin" && (
                    <button
                      onClick={() =>
                        openDeleteModal(user.id, getDisplayName(user))
                      }
                      disabled={deletingId === user.id}
                      className="px-3 py-1 bg-red-600 text-white rounded text-sm flex items-center gap-1 disabled:opacity-50"
                    >
                      <TrashIcon className="h-4 w-4" />
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-6 gap-2">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded bg-gray-200 disabled:opacity-50"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
              {getPageNumbers().map((page) => (
                <button
                  key={page}
                  onClick={() => paginate(page)}
                  className={`px-3 py-1 rounded ${
                    currentPage === page ? "bg-black text-white" : "bg-gray-200"
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2 rounded bg-gray-200 disabled:opacity-50"
              >
                <ChevronRightIcon className="h-5 w-5" />
              </button>
            </div>
          )}
        </>
      )}

      {/* Permission Override Modal with Loading */}
      {isPermissionsModalOpen && selectedUserForPerms && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto custom-scrollbar">
            <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white z-10">
              <h2 className="text-2xl font-semibold text-gray-700">
                Permissions for user{" "}
                <span className="text-2xl font-bold text-gray-900">
                  {getDisplayEmail(selectedUserForPerms)}
                </span>
              </h2>
              <button
                onClick={() => setIsPermissionsModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6">
              {modalLoading ? (
                <div className="text-center py-12">
                  <ArrowPathIcon className="mx-auto h-10 w-10 text-black animate-spin" />
                  <p className="mt-4 text-gray-600">Loading permissions...</p>
                </div>
              ) : permissionColumns.length === 0 ? (
                <p className="text-center text-gray-600 py-8">
                  No permissions available
                </p>
              ) : (
                <div className="space-y-6">
                  {MODULE_GROUPS.map((group) => {
                    const modulePerm = permissionColumns.find(
                      (p) => p.resource === group.moduleResource,
                    );
                    if (!modulePerm) return null;

                    return (
                      <section
                        key={group.key}
                        className="border border-gray-200 rounded-lg p-4"
                      >
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 border-b pb-3 mb-3">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                              {group.label}
                            </h3>
                            <p className="text-xs text-gray-500">
                              Module-level access
                            </p>
                          </div>
                          <AccessSelector
                            name={group.label}
                            value={userPermissionChanges[group.moduleResource]}
                            onChange={(access) =>
                              handleModuleCascade(group.moduleResource, access)
                            }
                          />
                        </div>

                        <div className="space-y-3">
                          {group.items.map((item) => {
                            const itemPerm = permissionColumns.find(
                              (p) => p.resource === item.resource,
                            );
                            if (!itemPerm) return null;

                            return (
                              <div
                                key={item.resource}
                                className="flex flex-col md:flex-row md:items-center md:justify-between gap-2"
                              >
                                <span className="text-sm text-gray-800">
                                  {item.label}
                                </span>
                                <AccessSelector
                                  name={item.label}
                                  value={userPermissionChanges[item.resource]}
                                  onChange={(access) =>
                                    handleAccessChangeInModal(
                                      item.resource,
                                      access,
                                    )
                                  }
                                />
                              </div>
                            );
                          })}
                        </div>
                      </section>
                    );
                  })}
                </div>
              )}
            </div>

            {!modalLoading && permissionColumns.length > 0 && (
              <div className="p-6 border-t flex justify-end gap-4 sticky bottom-0 bg-white z-10">
                <button
                  onClick={() => setIsPermissionsModalOpen(false)}
                  className="px-5 py-2.5 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveUserPermissions}
                  disabled={!canManagePermissions}
                  className="px-5 py-2.5 bg-black text-white rounded-lg hover:bg-gray-800"
                >
                  Save Permissions
                </button>
              </div>
            )}
          </div>

          <style>{`
            /* Chrome, Edge, Safari */
            .custom-scrollbar::-webkit-scrollbar {
              width: 8px;
            }
            .custom-scrollbar::-webkit-scrollbar-track {
              background: transparent;
              margin: 8px 0;
            }
            .custom-scrollbar::-webkit-scrollbar-thumb {
              background-color: rgba(0, 0, 0, 0.25);
              border-radius: 9999px;
              border: 2px solid transparent;
              background-clip: padding-box;
            }
            .custom-scrollbar::-webkit-scrollbar-thumb:hover {
              background-color: rgba(0, 0, 0, 0.4);
            }
            /* Firefox */
            .custom-scrollbar {
              scrollbar-width: thin;
              scrollbar-color: rgba(0, 0, 0, 0.35) transparent;
            }
          `}</style>
        </div>
      )}

      {/* Other Modals */}
      {isInviteModalOpen && (
        <InviteUserModal
          isOpen={isInviteModalOpen}
          onClose={() => setIsInviteModalOpen(false)}
          onInviteSent={fetchUsers}
        />
      )}
      {isUpdateModalOpen && selectedUser && (
        <UpdateProfileModal
          isOpen={isUpdateModalOpen}
          onClose={() => setIsUpdateModalOpen(false)}
          onProfileUpdated={fetchUsers}
          user={selectedUser}
        />
      )}
      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteConfirm}
        userName={deleteModal.userName}
        loading={deletingId !== null}
      />
    </div>
  );
};

const RolePermissionsTab = ({ refreshTrigger }) => {
  const [permissions, setPermissions] = useState([]);
  const [permissionColumns, setPermissionColumns] = useState([]);
  const [roleChanges, setRoleChanges] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const toCamelCase = (str) =>
    str
      .toLowerCase()
      .replace(/(?:^\w|[A-Z]|\b\w)/g, (w, i) =>
        i === 0 ? w.toLowerCase() : w.toUpperCase(),
      )
      .replace(/\s+/g, "")
      .replace(/[^a-zA-Z0-9]/g, "");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const permRes = await getPermissions();
      if (!permRes.data || !Array.isArray(permRes.data)) {
        toast.error("No permissions received");
        return;
      }
      const cols = permRes.data
        .filter((p) => p.resource !== "View Dashboard")
        .map((p) => ({
          key: `can${toCamelCase(p.resource)}`,
          label: p.resource,
          description: p.description,
          id: p._id,
        }));
      setPermissionColumns(cols);

      let roles = defaultRoles;
      try {
        const roleRes = await getRolePermissions();
        if (roleRes.data && Array.isArray(roleRes.data)) {
          roles = roleRes.data
            .map((r) => {
              const obj = { role: r.role };
              cols.forEach((col) => {
                const found = r.permissions.find(
                  (p) => p.permissionId === col.id,
                );
                obj[col.key] = found ? found.allowed : false;
              });
              return obj;
            })
            .filter((r) => r.role.toLowerCase() !== "admin");
        }
      } catch {}
      setPermissions(roles);
    } catch {
      toast.error("Failed to load role permissions");
      setPermissions(defaultRoles);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshTrigger]);

  const handleToggle = (roleName, key) => {
    setPermissions((prev) =>
      prev.map((r) => (r.role === roleName ? { ...r, [key]: !r[key] } : r)),
    );
    setRoleChanges(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await createRolePermission({
        roles: permissions.map((r) => ({
          role: r.role.toLowerCase(),
          permissions: permissionColumns.map((col) => ({
            permissionId: col.id,
            allowed: r[col.key] || false,
          })),
        })),
      });
      toast.success("Role permissions saved");
      setRoleChanges(false);
      fetchData();
    } catch {
      toast.error("Failed to save role permissions");
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <ArrowPathIcon className="mx-auto h-10 w-10 text-black animate-spin" />
        <p className="mt-4 text-gray-600">Loading role permissions...</p>
      </div>
    );
  }

  return (
    <section className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-6 border-b border-gray-200 flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-gray-900">
          Role Permissions
        </h2>
        <button
          onClick={handleSave}
          disabled={!roleChanges || isSaving}
          className={`px-6 py-3 rounded-lg font-medium text-white transition ${
            roleChanges && !isSaving
              ? "bg-black hover:bg-gray-800"
              : "bg-gray-400 cursor-not-allowed"
          }`}
        >
          {isSaving ? "Saving..." : "Save Changes"}
        </button>
      </div>
      <div className="p-6">
        <PermissionsTable
          items={permissions}
          columns={permissionColumns}
          onToggle={handleToggle}
          itemKey="role"
          displayKey="role"
          transposed={true}
        />
      </div>
    </section>
  );
};

export default UserAndPermissionsManagement;